/**
 * /financeiro/conciliacao-bancaria — Conciliação visual (M4) · redesign FIN-7.
 *
 * Fiel ao mockup Fase 5 V2.2 (#conciliacao): KPI strip + lista de pares
 * sugeridos lado-a-lado (.recon, ERP × extrato com score) + grid 2-colunas
 * com desajustes (lançado sem extrato / extrato sem lançamento).
 *
 * Camada visual; consome /api/financeiro/conciliacao/sugestoes + conciliar-par
 * que já existem. Nenhuma lógica nova. "Importar OFX" / "Criar lançamento"
 * reusam os painéis de /financeiro/lancamentos via deep-link (?ofx / ?novo).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiArrowRight, FiLink, FiUpload } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  conciliarPar, formatarReais, listarContas, obterSugestoesConciliacao,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function ddmm(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}
function sinalValor(l) {
  const v = formatarReais(l.valor);
  return l.tipo === "ENTRADA" ? `+${v}` : `−${v}`;
}

const JANELAS = [1, 3, 5, 7];


export default function ConciliacaoBancaria() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const hoje = useMemo(() => new Date(), []);

  const [contas, setContas] = useState([]);
  const [contaId, setContaId] = useState("");
  const [janelaDias, setJanelaDias] = useState(3);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inicio = useMemo(() => isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)), [hoje]);
  const fim = useMemo(() => isoLocal(hoje), [hoje]);
  const podeConciliar = isMaster || hasAllPermissions(["financeiro:conciliar"]);

  useEffect(() => {
    listarContas(request, { ativo: true }).then((p) => {
      const list = Array.isArray(p) ? p : p?.results || [];
      setContas(list);
      setContaId((atual) => atual || (list[0]?.id ?? ""));
    }).catch(() => {});
  }, [request]);

  const carregar = useCallback(async () => {
    if (!contaId) { setDados(null); return; }
    setLoading(true); setError(null);
    try {
      const d = await obterSugestoesConciliacao(request, {
        conta_id: contaId, inicio, fim, janela_dias: janelaDias,
      });
      setDados(d);
    } catch (err) {
      setError(err?.message || "Erro ao carregar sugestões.");
    } finally { setLoading(false); }
  }, [request, contaId, inicio, fim, janelaDias]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleConciliar = async (manual_id, ofx_id) => {
    await conciliarPar(request, manual_id, ofx_id);
    await carregar();
  };

  const sugestoes = dados?.sugestoes || [];
  const manuais = dados?.manuais_sem_par || [];
  const ofx = dados?.ofx_sem_par || [];
  const contaNome = contas.find((c) => c.id === contaId)?.nome || "—";

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Conciliação</span>
          </div>
          <h1 className="page-title">Conciliação bancária</h1>
          <div className="page-sub">Pareamento extrato × lançamentos · {contaNome} · período {ddmm(inicio)}–{ddmm(fim)}</div>
        </div>
        <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
          <select className="select" value={contaId} style={{ maxWidth: 200, height: 34 }}
                  onChange={(e) => setContaId(e.target.value)} aria-label="Conta">
            {contas.length === 0 && <option value="">— Conta —</option>}
            {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <select className="select" value={janelaDias} style={{ maxWidth: 130, height: 34 }}
                  onChange={(e) => setJanelaDias(parseInt(e.target.value, 10) || 3)} aria-label="Janela de dias">
            {JANELAS.map((j) => <option key={j} value={j}>Janela · {j} dia{j > 1 ? "s" : ""}</option>)}
          </select>
          <button type="button" className="btn btn--ghost"
                  onClick={() => navigate("/financeiro/lancamentos?ofx=1")}>
            <FiUpload size={14} aria-hidden="true" /> Importar OFX
          </button>
        </div>
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Pares sugeridos</div><div className="val">{sugestoes.length}</div><div className="meta">score ≥ 50%</div></div>
        <div className="kpi-v2"><div className="lbl">Lançados sem extrato</div><div className="val" style={{ color: manuais.length ? "var(--warning-text)" : undefined }}>{manuais.length}</div><div className="meta">aguardando crédito</div></div>
        <div className="kpi-v2"><div className="lbl">Extrato sem par</div><div className="val" style={{ color: ofx.length ? "var(--warning-text)" : undefined }}>{ofx.length}</div><div className="meta">criar lançamento</div></div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="block" style={{ marginBottom: 14 }}>
        <div className="block__head">
          <div className="block__title">Pares sugeridos</div>
          <span className="tiny muted">confirme para conciliar</span>
        </div>
        <div className="block__body tight">
          {sugestoes.length === 0 ? (
            <div className="empty"><div className="msg">
              {loading ? "Carregando…" : "Nenhuma sugestão de par. Precisa de lançamento manual (sem FITID) e do extrato OFX (com FITID) na mesma conta, valor e janela."}
            </div></div>
          ) : sugestoes.map((s) => {
            const pct = Math.round((s.score || 0) * 100);
            const alta = (s.score || 0) >= 0.9;
            return (
              <div key={`${s.manual.id}-${s.ofx.id}`} className="recon__row" style={{ padding: "14px 18px", borderBottom: "1px dashed var(--divider)" }}>
                <div className="recon__side">
                  <div className="recon__lbl">Lançamento ERP</div>
                  <div className="recon__main ell" title={s.manual.descricao}>{s.manual.descricao}</div>
                  <div className="recon__meta mono">{ddmm(s.manual.data_competencia)} · {sinalValor(s.manual)}</div>
                </div>
                <div className="recon__match">
                  <span className={"score " + (alta ? "high" : "mid")}>{pct}%</span>
                  <FiArrowRight size={16} aria-hidden="true" style={{ color: "var(--ink-4)", marginTop: 4 }} />
                </div>
                <div className="recon__side">
                  <div className="recon__lbl">Extrato {contaNome}</div>
                  <div className="recon__main mono ell" title={s.ofx.descricao}>{s.ofx.descricao}</div>
                  <div className="recon__meta mono">{ddmm(s.ofx.data_competencia)} · {sinalValor(s.ofx)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {podeConciliar && (
                    <AsyncButton variant={alta ? "primary" : "secondary"}
                                 style={{ padding: "6px 12px", fontSize: 12 }}
                                 successMessage="Par conciliado"
                                 onClick={() => handleConciliar(s.manual.id, s.ofx.id)}>
                      <FiLink size={12} aria-hidden="true" /> Confirmar par
                    </AsyncButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-grid-2">
        <div className="block">
          <div className="block__head">
            <div className="block__title">Lançados no ERP sem extrato</div>
            <span className="tiny mono muted">{manuais.length}</span>
          </div>
          <div className="block__body tight">
            {manuais.length === 0 ? (
              <div className="empty"><div className="msg">Tudo conciliado.</div></div>
            ) : manuais.map((l) => (
              <div key={l.id} className="unmatched" style={{ padding: "10px 18px" }}>
                <div>
                  <div className="um-main ell" title={l.descricao}>{l.descricao}</div>
                  <div className="um-meta mono">{ddmm(l.data_competencia)} · {sinalValor(l)}{l.confirmado ? "" : " · previsto"}</div>
                </div>
                <span className="tiny muted">aguardando crédito</span>
              </div>
            ))}
          </div>
        </div>

        <div className="block">
          <div className="block__head">
            <div className="block__title">No extrato sem lançamento</div>
            <span className="tiny mono muted">{ofx.length}</span>
          </div>
          <div className="block__body tight">
            {ofx.length === 0 ? (
              <div className="empty"><div className="msg">Tudo conciliado.</div></div>
            ) : ofx.map((l) => (
              <div key={l.id} className="unmatched" style={{ padding: "10px 18px" }}>
                <div>
                  <div className="um-main mono ell" title={l.descricao}>{l.descricao}</div>
                  <div className="um-meta mono">{ddmm(l.data_competencia)} · {sinalValor(l)}</div>
                </div>
                <button type="button" className="btn btn--secondary btn--sm"
                        onClick={() => navigate("/financeiro/lancamentos?novo=1")}>Criar lançamento</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
